import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import { blogArticles } from "../../data/blogArticles";
import { ArrowLongLeftIcon } from "@heroicons/react/24/outline";

function renderMarkdown(text) {
  // Very minimal markdown-to-JSX renderer for our blog content
  const lines = text.trim().split("\n");
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Heading ##
    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={key++}
          className="text-2xl font-bold text-gray-900 mt-10 mb-4"
        >
          {line.slice(3)}
        </h2>
      );
      i++;
      continue;
    }

    // Heading ###
    if (line.startsWith("### ")) {
      elements.push(
        <h3
          key={key++}
          className="text-xl font-bold text-gray-900 mt-8 mb-3"
        >
          {line.slice(4)}
        </h3>
      );
      i++;
      continue;
    }

    // List items
    if (line.startsWith("- ")) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        listItems.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={key++} className="list-disc pl-6 space-y-2 my-4">
          {listItems.map((item, j) => (
            <li key={j} className="text-gray-600 leading-relaxed">
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Empty lines
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} className="text-gray-600 leading-relaxed mb-4">
        <InlineMarkdown text={line} />
      </p>
    );
    i++;
  }

  return elements;
}

function InlineMarkdown({ text }) {
  // Handle **bold**, [links](url), and regular text
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Link
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    let firstMatchIndex = Infinity;
    let firstMatch = null;
    let matchType = null;

    if (boldMatch && boldMatch.index < firstMatchIndex) {
      firstMatchIndex = boldMatch.index;
      firstMatch = boldMatch;
      matchType = "bold";
    }
    if (linkMatch && linkMatch.index < firstMatchIndex) {
      firstMatchIndex = linkMatch.index;
      firstMatch = linkMatch;
      matchType = "link";
    }

    if (!firstMatch) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    // Text before the match
    if (firstMatchIndex > 0) {
      parts.push(
        <span key={key++}>{remaining.slice(0, firstMatchIndex)}</span>
      );
    }

    if (matchType === "bold") {
      parts.push(
        <strong key={key++} className="font-semibold text-gray-900">
          {firstMatch[1]}
        </strong>
      );
    } else if (matchType === "link") {
      const href = firstMatch[2];
      if (href.startsWith("/")) {
        parts.push(
          <Link
            key={key++}
            to={href}
            className="text-[#0186ef] hover:underline font-medium"
          >
            {firstMatch[1]}
          </Link>
        );
      } else {
        parts.push(
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0186ef] hover:underline font-medium"
          >
            {firstMatch[1]}
          </a>
        );
      }
    }

    remaining = remaining.slice(firstMatchIndex + firstMatch[0].length);
  }

  return <>{parts}</>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  // Get other articles for sidebar
  const otherArticles = blogArticles.filter((a) => a.id !== article.id);

  return (
    <>
      <Navbar />

      <article className="pt-32 lg:pt-40 pb-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-16">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0186ef] transition-colors mb-10"
          >
            <ArrowLongLeftIcon className="w-4 h-4" />
            Back to home
          </Link>

          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex px-3 py-1 bg-blue-50 text-[#0186ef] rounded-full text-xs font-semibold border border-blue-100">
                {article.category}
              </span>
              <span className="text-sm text-gray-400">{article.readTime}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-4">
              {article.title}
            </h1>
            <time className="text-sm text-gray-400">{article.date}</time>
          </div>

          {/* Featured image */}
          <div className="aspect-[2/1] rounded-2xl overflow-hidden mb-12">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="max-w-3xl">{renderMarkdown(article.content)}</div>

          {/* Divider */}
          <hr className="my-16 border-gray-200" />

          {/* More articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              More articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {otherArticles.map((other) => (
                <Link
                  key={other.id}
                  to={`/blog/${other.slug}`}
                  className="group"
                >
                  <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                    <img
                      src={other.image}
                      alt={other.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="text-xs font-semibold text-[#0186ef]">
                    {other.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 group-hover:text-[#0186ef] transition-colors leading-snug">
                    {other.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
}
